import React, {useEffect, useMemo, useState} from "react";
import {useParams, useNavigate, useSearchParams} from "react-router-dom";
import {supabase} from "./utils/supabaseClient";
import { useAuth } from "./providers/AuthProvider";
import {Box, Button, Center, Heading, Text, VStack, HStack, Divider, Link, Icon, Spinner, Container, Tabs, TabList, TabPanels, Tab, TabPanel, Badge, Flex, Table, Thead, Tbody, Tr, Th, Td, IconButton, useDisclosure, SimpleGrid, Tooltip, Alert, AlertDescription, AlertIcon, AlertTitle, CloseButton} from "@chakra-ui/react";
import { FiArrowLeft, FiBookOpen, FiClipboard, FiInfo, FiMail, FiDollarSign, FiEye, FiFileText, FiDownload, FiBell, FiStar } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import {saveAs} from "file-saver";
import {motion} from "framer-motion";
import moment from "moment";
import { useMediaQuery, useToast } from "@chakra-ui/react";
import { getParticipantFullName, formatCurrency, formatInvoiceDate, getDueInBadge } from "./utils/formatHelpers";
import { generateICSContent, generateMultipleICSContent, downloadICS, formatICSDate, formatICSDateTime } from "./utils/calendarHelpers";
import { CourseOverviewTab } from "./components/CourseOverviewTab";
import { CourseResourcesTab } from "./components/CourseResourcesTab";
import { AssessmentResourcesTab } from "./components/AssessmentResourcesTab";
import { CourseEvaluationTab } from "./components/CourseEvaluationTab";
import { NoticesTab } from "./components/NoticesTab";
import { InvoicePdfModal } from "./components/InvoicePdfModal";
import { getStoredViewAsStudent } from "./utils/staffHelpers";
import { resolveMeetingUrl } from "./utils/meetingHelpers";
import {
    fetchEvaluationResponse,
    fetchPublishedEvaluationForm,
    isEvaluationOpenForCourseRun,
    resolveRegistrationForCourseRun,
} from "./utils/lmsEvaluations";

const TAB_KEYS = ["overview", "materials", "assessments", "documents", "invoice", "evaluation", "notices"];

export const EachCourse = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const [courseMaterials, setCourseMaterials] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [enrollmentDocs, setEnrollmentDocs] = useState([]);
    const [downloadingDoc, setDownloadingDoc] = useState(null);
    const [viewingInvoice, setViewingInvoice] = useState(null);
    const { isOpen: isInvoiceOpen, onOpen: onInvoiceOpen, onClose: onInvoiceClose } = useDisclosure();
    const {courseRunId} = useParams();
    const [isMobile] = useMediaQuery("(max-width: 768px)");
    const toast = useToast();
    const [registration, setRegistration] = useState(null);
    const [evaluationDue, setEvaluationDue] = useState(false);
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const [evalRefreshKey, setEvalRefreshKey] = useState(0);

    const tabIndex = useMemo(() => {
        const key = (searchParams.get("tab") || "overview").toLowerCase();
        const idx = TAB_KEYS.indexOf(key);
        return idx >= 0 ? idx : 0;
    }, [searchParams]);

    const setTabIndex = (index) => {
        const key = TAB_KEYS[index] || "overview";
        const next = new URLSearchParams(searchParams);
        if (key === "overview") next.delete("tab");
        else next.set("tab", key);
        setSearchParams(next, { replace: true });
    };

    const handleDownloadSessionCalendar = (schedule, moduleName, allSessions) => {
        const dateStr = moment(schedule.date).format("YYYY-MM-DD");
        const sessionForICS = { date: dateStr, start_time: schedule.start_time, end_time: schedule.end_time, venue: schedule.venue, venueAddress: schedule.venueAddress, room: schedule.room, session_id: schedule.session_id || schedule.date };
        let sessionNumber = null, totalSessions = null;
        if (allSessions && Array.isArray(allSessions)) {
            const sorted = [...allSessions].sort((a, b) => moment(a.date).diff(moment(b.date)));
            const idx = sorted.findIndex(s => moment(s.date).format("YYYY-MM-DD") === dateStr && s.start_time === schedule.start_time);
            if (idx !== -1) { sessionNumber = idx + 1; totalSessions = sorted.length; }
        }
        downloadICS(generateICSContent(sessionForICS, moduleName, sessionNumber, totalSessions), `${moduleName.replace(/\s+/g, "_")}_${moment(schedule.date).format("YYYYMMDD")}.ics`);
        if (!isMobile) { toast({ title: "Calendar Event Downloaded", description: "Open the .ics file to add to your calendar.", status: "success", duration: 3000, isClosable: true }); }
    };

    const handleDownloadAllSessionsCalendar = () => {
        if (!courseMaterials?.course_dates || courseMaterials.course_dates.length === 0) {
            toast({ title: "No Sessions", description: "No scheduled sessions yet.", status: "warning", duration: 3000, isClosable: true }); return;
        }
        const moduleName = courseMaterials?.course_type?.name || "Module";
        const sessions = courseMaterials.course_dates.map(cd => ({ date: moment(cd.date).format("YYYY-MM-DD"), start_time: cd.start_time, end_time: cd.end_time, venue: cd.venue, venueAddress: cd.venueAddress, room: cd.room, session_id: cd.date }));
        downloadICS(generateMultipleICSContent(sessions, moduleName), `${moduleName.replace(/\s+/g, "_")}_All_Sessions.ics`);
        if (!isMobile) { toast({ title: "Calendar Events Downloaded", description: `${sessions.length} sessions downloaded.`, status: "success", duration: 4000, isClosable: true }); }
    };

    // Prefilled enquiry links
    const courseNameForEnquiry = courseMaterials?.course_type?.name || "this course";
    const courseRunCodeForEnquiry = courseMaterials?.id || "\u2014";
    const courseRunNameForEnquiry = courseMaterials?.course_type?.name || courseNameForEnquiry;
    const getEnquiryMessage = () => {
        const participantName = getParticipantFullName(user);
        return `Hello, I am ${participantName} currently enrolled in ${courseNameForEnquiry}, I have question(s) about ${courseRunNameForEnquiry} (${courseRunCodeForEnquiry}):\n\nMy questions is/are:\n1) `;
    };
    const getMailtoEnquiryUrl = (email) => {
        if (!email) return null;
        const subject = encodeURIComponent(`Enquiry: ${courseRunNameForEnquiry} (${courseRunCodeForEnquiry})`);
        const body = encodeURIComponent(getEnquiryMessage());
        return `mailto:${email}?subject=${subject}&body=${body}`;
    };
    const getWhatsAppEnquiryUrl = (phone) => {
        if (!phone || typeof phone !== "string") return null;
        const digits = phone.replace(/\D/g, "");
        const normalized = digits.length === 8 && /^[89]/.test(digits) ? `65${digits}` : digits.startsWith("65") ? digits : digits.length >= 8 ? `65${digits.slice(-8)}` : digits;
        if (normalized.length < 8) return null;
        const text = encodeURIComponent(getEnquiryMessage());
        return `https://wa.me/${normalized}?text=${text}`;
    };

    const getAllCourseMaterials = async () => {
        const {data, error } = await supabase
            .from("course_run")
            .select(`
                id,
                course_type_code,
                start_date,
                end_date,
                venue,
                attachment,
                attendance_req,
                max_capacity,
                is_assessed,
                assessment_workflow_status,
                is_course_run_confirmed,
                trainer_id,
                venue_id,
                room_id,
                trainers (
                    name,
                    email
                ),
                venues (
                    name,
                    address,
                    postal_code,
                    phone,
                    contact_person
                ),
                rooms (
                    name,
                    capacity,
                    floor,
                    unit
                ),
                course_type (
                    code,
                    name,
                    short_desc,
                    course_resources,
                    assessment_resources,
                    course_objectives,
                    course_duration,
                    minimum_entry_requirements,
                    prerequisites,
                    price,
                    certification,
                    school
                )
            `)
            .eq("id", courseRunId) 
            .single();

        if (data) {
            const { data: sessions, error: sessionsError } = await supabase
                .from("session")
                .select("*")
                .eq("course_run_code", courseRunId)
                .order("session_date", { ascending: true });

            if (sessionsError) {
                console.error("Error fetching sessions:", sessionsError);
            }

            const roomIds = [...new Set((sessions || []).filter(s => s.room_id && !s.custom_room).map(s => s.room_id))];
            const roomMap = {};
            if (roomIds.length > 0) {
                const { data: rooms } = await supabase
                    .from("rooms")
                    .select("code, name, floor, unit")
                    .in("code", roomIds);
                rooms?.forEach(r => { roomMap[r.code] = r; });
            }

            const venueIds = [...new Set((sessions || []).filter(s => s.venue_id && !s.custom_venue).map(s => s.venue_id))];
            const venueMap = {};
            if (venueIds.length > 0) {
                const { data: venues } = await supabase
                    .from("venues")
                    .select("code, name, address, is_online")
                    .in("code", venueIds);
                venues?.forEach(v => { venueMap[v.code] = v; });
            }

            const course_dates = (sessions || []).map(session => {
                const roomName = session.custom_room || (session.room_id && roomMap[session.room_id]?.name) || session.room_id;
                const venueObj = session.venue_id && venueMap[session.venue_id];
                const venueName = session.custom_venue || (venueObj?.name) || session.venue_id;
                const meetingUrl = resolveMeetingUrl(session);
                const venueAddress = venueObj?.is_online ? "" : (venueObj?.address || "");
                return {
                    date: session.session_date,
                    start_time: session.start_time ? moment(session.start_time, "HH:mm:ss").format("HHmm") : null,
                    end_time: session.end_time ? moment(session.end_time, "HH:mm:ss").format("HHmm") : null,
                    room: roomName,
                    venue: venueName,
                    venueAddress,
                    session_id: session.session_id || session.id,
                    meeting_url: meetingUrl,
                    is_online: !!session.is_online || !!venueObj?.is_online || !!meetingUrl,
                    online_meeting_details: session.online_meeting_details || null,
                };
            });

            setCourseMaterials({
                ...data,
                course_dates: course_dates
            });
        }
        if (error) {
            console.error("Error fetching course materials:", error);
        }
    };

    // Lookup the user's registration ID for this course run.
    // Prefers the most "complete" stage when multiple registrations exist for the same course.
    const resolveRegistrationId = async () => {
        // Earlier index = higher priority. Unknown stages get 999 (lowest).
        const STAGE_PRIORITY = [
            "payment_received", "enrolled", "documents_pending",
            "registration_fee_pending", "payment_pending", "draft", "pending", "rejected",
        ];
        const pick = (rows) => {
            if (!rows || rows.length === 0) return null;
            if (rows.length === 1) return rows[0].id;
            const sorted = [...rows].sort((a, b) => {
                const ai = STAGE_PRIORITY.indexOf(a.registration_stage);
                const bi = STAGE_PRIORITY.indexOf(b.registration_stage);
                return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
            });
            return sorted[0].id;
        };

        const preview = getStoredViewAsStudent();
        const targetId = preview?.userId || (!preview ? user?.id : null);
        const targetEmail = preview?.email || (!preview ? user?.email : null);

        if (targetId) {
            const { data } = await supabase
                .from("registration")
                .select("id, registration_stage")
                .eq("draft_course_run_code", courseRunId)
                .eq("user_id", targetId);
            const id = pick(data);
            if (id) return id;
        }
        if (targetEmail) {
            const { data } = await supabase
                .from("registration")
                .select("id, registration_stage")
                .eq("draft_course_run_code", courseRunId)
                .ilike("participant_email", targetEmail);
            const id = pick(data);
            if (id) return id;
        }
        return null;
    };

    const fetchInvoicesAndDocs = async () => {
        const preview = getStoredViewAsStudent();
        const targetId = preview?.userId || (!preview ? user?.id : null);
        const targetEmail = preview?.email || (!preview ? user?.email : null);
        if (!targetId && !targetEmail) return;
        const registrationId = await resolveRegistrationId();
        if (!registrationId) return;

        // Invoices
        const { data: invData } = await supabase
            .from("invoices")
            .select(`id, invoice_type, invoice_date, due_date, status, gst_amount, pdf_url, amount_paid,
                invoice_line_items (id, description, quantity, unit_price)`)
            .eq("registration_id", registrationId)
            .order("invoice_date", { ascending: true });
        setInvoices(invData || []);

        // Enrollment documents
        const { data: allTemplates } = await supabase
            .from("google_document_templates")
            .select("id, name, section")
            .eq("is_active", true)
            .order("name", { ascending: true });

        const allowedSections = ["Initial Registration", "Registration", "Default"];
        const filteredTemplates = (allTemplates || []).filter(t => allowedSections.includes(t.section || "Default"));

        if (filteredTemplates.length > 0) {
            const { data: allDocs } = await supabase
                .from("document_generator_docs")
                .select("id, document_name, pdf_file_id, template_id, template:template_id (name)")
                .eq("registration_id", registrationId)
                .order("created_at", { ascending: true });

            const docsByTemplate = new Map();
            (allDocs || []).forEach(doc => { if (doc.template_id) docsByTemplate.set(doc.template_id, doc); });

            const documentsList = filteredTemplates.map(template => {
                const doc = docsByTemplate.get(template.id);
                if (doc && doc.pdf_file_id) {
                    return {
                        id: doc.id, templateId: template.id,
                        title: doc.document_name || doc.template?.name || template.name,
                        pdfFileId: doc.pdf_file_id,
                        pdfUrl: `https://drive.google.com/file/d/${doc.pdf_file_id}/preview`,
                        pdfDownloadUrl: `https://drive.google.com/uc?export=download&id=${doc.pdf_file_id}`,
                        isPdf: true, status: "done",
                    };
                } else if (doc && !doc.pdf_file_id) {
                    return { id: doc.id, templateId: template.id, title: doc.document_name || template.name, isPdf: false, status: "generating" };
                } else {
                    return { id: `template-${template.id}`, templateId: template.id, title: template.name, isPdf: false, status: "not_started" };
                }
            });
            setEnrollmentDocs(documentsList);
        }
    };

    const handleViewInvoice = (invoice) => {
        setViewingInvoice(invoice);
        onInvoiceOpen();
    };

    const handleViewDocument = (doc) => {
        if (doc.isPdf && doc.pdfUrl) {
            window.open(doc.pdfUrl, "_blank", "noopener,noreferrer");
        }
    };

    const handleDownloadDocument = async (doc) => {
        if (!doc.isPdf || !doc.pdfDownloadUrl) return;
        setDownloadingDoc(doc.id);
        try {
            const link = document.createElement("a");
            link.href = doc.pdfDownloadUrl;
            link.download = `${doc.title}.pdf`;
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            if (!isMobile) toast({ title: "Download Started", description: `Downloading ${doc.title}...`, status: "success", duration: 3000, isClosable: true });
        } catch (e) {
            toast({ title: "Download Error", description: "Failed to download document.", status: "error", duration: 3000, isClosable: true });
        } finally {
            setDownloadingDoc(null);
        }
    };

    const downloadFile = async (filePath) => {
        try {
            const { data, error } = await supabase.storage.from("Course Files").download(filePath);
            if (error) throw error;
            const fileName = filePath.split("/").pop();
            saveAs(data, fileName);
        } catch (error) {
            console.error("Error downloading file:", error);
        }
    };

    useEffect(() => {
        getAllCourseMaterials();
        fetchInvoicesAndDocs();
    }, [courseRunId, user?.id, user?.email])

    useEffect(() => {
        let cancelled = false;
        const loadEvalState = async () => {
            if (!courseRunId || !user) {
                setRegistration(null);
                setEvaluationDue(false);
                return;
            }
            const preview = getStoredViewAsStudent();
            const targetUserId = preview?.userId || user.id;
            const targetEmail = preview?.email || user.email;

            const reg = await resolveRegistrationForCourseRun({
                userId: targetUserId,
                email: targetEmail,
                courseRunId,
            });
            if (cancelled) return;
            setRegistration(reg);

            const form = await fetchPublishedEvaluationForm();
            if (cancelled) return;
            if (!form || !reg?.id) {
                setEvaluationDue(false);
                return;
            }
            const runForAvailability = courseMaterials?.id === courseRunId
                ? courseMaterials
                : { id: courseRunId, end_date: courseMaterials?.end_date };
            if (!isEvaluationOpenForCourseRun(form, runForAvailability)) {
                setEvaluationDue(false);
                return;
            }
            const existing = await fetchEvaluationResponse({
                formId: form.id,
                registrationId: reg.id,
                courseRunId,
            });
            if (cancelled) return;
            setEvaluationDue(!existing);
        };
        loadEvalState();
        return () => {
            cancelled = true;
        };
    }, [courseRunId, user?.id, user?.email, evalRefreshKey, courseMaterials?.id, courseMaterials?.end_date]);

    if (!courseMaterials) {
        return (
            <Center minH="100vh">
                <Spinner size="xl" />
            </Center>
        );
    }
    
    // Parse resources safely
    let courseResources = [];
    let assessmentResources = [];
    
    try {
        if (courseMaterials?.course_type?.course_resources) {
            const parsed = typeof courseMaterials.course_type.course_resources === 'string' 
                ? JSON.parse(courseMaterials.course_type.course_resources)
                : courseMaterials.course_type.course_resources;
            if (parsed && parsed.resources && Array.isArray(parsed.resources)) {
                courseResources = parsed.resources;
            } else if (Array.isArray(parsed)) {
                courseResources = parsed;
            } else {
                courseResources = [];
            }
        }
    } catch (e) {
        console.error('Error parsing course_resources:', e);
    }
    
    try {
        if (courseMaterials?.course_type?.assessment_resources) {
            const parsed = typeof courseMaterials.course_type.assessment_resources === 'string'
                ? JSON.parse(courseMaterials.course_type.assessment_resources)
                : courseMaterials.course_type.assessment_resources;
            assessmentResources = Array.isArray(parsed) ? parsed : [];
        }
    } catch (e) {
        console.error('Error parsing assessment_resources:', e);
    }
    
    return (
        <Box minH="100vh" py={8}>
            <InvoicePdfModal
                invoice={viewingInvoice}
                isOpen={isInvoiceOpen}
                onClose={onInvoiceClose}
            />
            <Container>
                <Button variant="outline" mb={5} leftIcon={<FiArrowLeft />} onClick={() => navigate("/enrolled-courses")}>
                    Back To Courses
                </Button>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Box bg="white" p={8} borderRadius="lg" boxShadow="md">
                        {/* Course Header */}
                        <VStack align="stretch" spacing={4} mb={6}>
                            <Flex justify="space-between" align="start" flexWrap="wrap">
                                <Box flex="1" minW="300px">
                                    <HStack mb={2}>
                                        <Badge colorScheme="teal" fontSize="sm" px={3} py={1}>
                                            {courseMaterials?.id || courseMaterials?.course_type?.code}
                                        </Badge>
                                        {courseMaterials?.is_course_run_confirmed && (
                                            <Badge colorScheme="green" fontSize="sm" px={3} py={1}>Confirmed</Badge>
                                        )}
                                        {courseMaterials?.is_assessed && (
                                            <Badge colorScheme="orange" fontSize="sm" px={3} py={1}>Assessed</Badge>
                                        )}
                                    </HStack>
                                    <Heading size="lg" mb={3} color="brand.700">
                                        {courseMaterials?.course_type?.name || 'Course'}
                                    </Heading>
                                </Box>
                            </Flex>
                        </VStack>

                        {evaluationDue && !bannerDismissed && TAB_KEYS[tabIndex] !== "evaluation" ? (
                            <Alert
                                status="warning"
                                variant="subtle"
                                borderRadius="xl"
                                mb={5}
                                alignItems="flex-start"
                            >
                                <AlertIcon mt={1} />
                                <Box flex="1">
                                    <AlertTitle fontSize="sm">Evaluation due</AlertTitle>
                                    <AlertDescription fontSize="sm" display="block">
                                        Please complete the course evaluation for this module.
                                    </AlertDescription>
                                    <Button
                                        mt={3}
                                        size="sm"
                                        colorScheme="orange"
                                        onClick={() => setTabIndex(TAB_KEYS.indexOf("evaluation"))}
                                    >
                                        Fill evaluation
                                    </Button>
                                </Box>
                                <CloseButton
                                    size="sm"
                                    onClick={() => setBannerDismissed(true)}
                                    aria-label="Dismiss evaluation banner"
                                />
                            </Alert>
                        ) : null}

                        <Tabs
                            colorScheme="brand"
                            isFitted
                            variant="enclosed"
                            isLazy
                            index={tabIndex}
                            onChange={setTabIndex}
                        >
                            <TabList
                                mb={6}
                                overflowX="auto"
                                overflowY="hidden"
                                css={{
                                    scrollbarWidth: 'none',
                                    '&::-webkit-scrollbar': { display: 'none' },
                                }}
                            >
                                <Tab fontSize={{ base: "xs", md: "sm" }} py={3} whiteSpace="nowrap">
                                    <Icon as={FiInfo} mr={{ base: 0, md: 2 }} />
                                    <Text display={{ base: "none", sm: "inline" }}>Overview</Text>
                                </Tab>
                                <Tab fontSize={{ base: "xs", md: "sm" }} py={3} whiteSpace="nowrap">
                                    <Icon as={FiBookOpen} mr={{ base: 0, md: 2 }} />
                                    <Text display={{ base: "none", sm: "inline" }}>Course Materials</Text>
                                </Tab>
                                <Tab fontSize={{ base: "xs", md: "sm" }} py={3} whiteSpace="nowrap">
                                    <Icon as={FiClipboard} mr={{ base: 0, md: 2 }} />
                                    <Text display={{ base: "none", sm: "inline" }}>Assessments</Text>
                                </Tab>
                                <Tab fontSize={{ base: "xs", md: "sm" }} py={3} whiteSpace="nowrap">
                                    <Icon as={FiFileText} mr={{ base: 0, md: 2 }} />
                                    <Text display={{ base: "none", sm: "inline" }}>Documents</Text>
                                    {enrollmentDocs.some(d => d.status === "done") && (
                                        <Badge ml={1} colorScheme="blue" fontSize="2xs" borderRadius="full" px={1.5}>{enrollmentDocs.filter(d => d.status === "done").length}</Badge>
                                    )}
                                </Tab>
                                <Tab fontSize={{ base: "xs", md: "sm" }} py={3} whiteSpace="nowrap">
                                    <Icon as={FiDollarSign} mr={{ base: 0, md: 2 }} />
                                    <Text display={{ base: "none", sm: "inline" }}>Invoice</Text>
                                    {invoices.some(inv => inv.status !== 'paid' && inv.status !== 'Paid') && (
                                        <Badge ml={1} colorScheme="orange" fontSize="2xs" borderRadius="full" px={1.5}>!</Badge>
                                    )}
                                </Tab>
                                <Tab fontSize={{ base: "xs", md: "sm" }} py={3} whiteSpace="nowrap">
                                    <Icon as={FiStar} mr={{ base: 0, md: 2 }} />
                                    <Text display={{ base: "none", sm: "inline" }}>Evaluation</Text>
                                    {evaluationDue ? (
                                        <Badge ml={1} colorScheme="orange" fontSize="2xs" borderRadius="full" px={1.5}>Due</Badge>
                                    ) : null}
                                </Tab>
                                <Tab fontSize={{ base: "xs", md: "sm" }} py={3} whiteSpace="nowrap">
                                    <Icon as={FiBell} mr={{ base: 0, md: 2 }} />
                                    <Text display={{ base: "none", sm: "inline" }}>Notices</Text>
                                </Tab>
                            </TabList>

                            <TabPanels>
                                <TabPanel px={0}>
                                    <CourseOverviewTab
                                        courseMaterials={courseMaterials}
                                        handleDownloadSessionCalendar={handleDownloadSessionCalendar}
                                        handleDownloadAllSessionsCalendar={handleDownloadAllSessionsCalendar}
                                    />
                                </TabPanel>
                                <TabPanel px={0}>
                                    <CourseResourcesTab
                                        courseResources={courseResources}
                                        downloadFile={downloadFile}
                                    />
                                </TabPanel>
                                <TabPanel px={0}>
                                    <AssessmentResourcesTab
                                        assessmentResources={assessmentResources}
                                        downloadFile={downloadFile}
                                    />
                                </TabPanel>
                                {/* Enrollment Documents tab */}
                                <TabPanel px={0}>
                                    {enrollmentDocs.length === 0 ? (
                                        <Box py={10} textAlign="center">
                                            <Icon as={FiFileText} boxSize={8} color="gray.300" mb={3} />
                                            <Text color="gray.500" fontSize="sm">No enrollment documents yet.</Text>
                                        </Box>
                                    ) : (
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                            {enrollmentDocs.map((doc) => {
                                                const isDone = doc.status === "done";
                                                const isGenerating = doc.status === "generating";
                                                return (
                                                    <Box key={doc.id} p={4} bg="white" borderRadius="md"
                                                        border="1px solid" borderColor={isDone ? "brand.200" : "gray.200"}
                                                        boxShadow="sm"
                                                    >
                                                        <Flex justify="space-between" align="start">
                                                            <HStack spacing={3} flex={1} minW={0}>
                                                                <Icon as={FiFileText} color={isDone ? "brand.500" : "gray.400"} boxSize={5} flexShrink={0} />
                                                                <Box minW={0}>
                                                                    <Text fontWeight="semibold" fontSize="sm" noOfLines={2}>{doc.title}</Text>
                                                                    <Badge mt={1} colorScheme={isDone ? "green" : isGenerating ? "yellow" : "gray"} fontSize="2xs">
                                                                        {isDone ? "Ready" : isGenerating ? "Generating…" : "Pending"}
                                                                    </Badge>
                                                                </Box>
                                                            </HStack>
                                                            {isDone && (
                                                                <HStack spacing={1} ml={2} flexShrink={0}>
                                                                    <Tooltip label="View" hasArrow>
                                                                        <IconButton icon={<FiEye />} size="sm" variant="ghost" colorScheme="brand"
                                                                            aria-label="View document" onClick={() => handleViewDocument(doc)} />
                                                                    </Tooltip>
                                                                    <Tooltip label="Download PDF" hasArrow>
                                                                        <IconButton icon={<FiDownload />} size="sm" variant="ghost" colorScheme="brand"
                                                                            aria-label="Download document" isLoading={downloadingDoc === doc.id}
                                                                            onClick={() => handleDownloadDocument(doc)} />
                                                                    </Tooltip>
                                                                </HStack>
                                                            )}
                                                        </Flex>
                                                    </Box>
                                                );
                                            })}
                                        </SimpleGrid>
                                    )}
                                </TabPanel>
                                <TabPanel px={0}>
                                    {invoices.length === 0 ? (
                                        <Box py={10} textAlign="center">
                                            <Icon as={FiFileText} boxSize={8} color="gray.300" mb={3} />
                                            <Text color="gray.500" fontSize="sm">No invoices found for this course.</Text>
                                        </Box>
                                    ) : (
                                        <VStack spacing={4} align="stretch">
                                            {/* Mobile card view */}
                                            <VStack spacing={2} align="stretch" display={{ base: "flex", md: "none" }}>
                                                {invoices.map((invoice) => {
                                                    const isPaid = invoice.status === 'paid' || invoice.status === 'Paid';
                                                    const subtotal = (invoice.invoice_line_items || []).reduce((s, i) => s + (i.unit_price * i.quantity), 0);
                                                    const total = subtotal + (invoice.gst_amount || 0);
                                                    const dueInBadge = getDueInBadge(invoice);
                                                    return (
                                                        <Box key={invoice.id} bg="white" p={3} borderRadius="md"
                                                            border="1px solid" borderColor={isPaid ? "green.200" : "gray.200"}
                                                            borderLeft={isPaid ? "4px solid" : "1px solid"} borderLeftColor={isPaid ? "green.400" : "orange.400"}
                                                            boxShadow="sm" cursor={invoice.pdf_url ? "pointer" : "default"}
                                                            onClick={() => invoice.pdf_url && handleViewInvoice(invoice)}
                                                            _hover={invoice.pdf_url ? { boxShadow: "md", transform: "translateY(-1px)" } : {}}
                                                            transition="all 0.2s"
                                                        >
                                                            <Flex justify="space-between" align="center" mb={2}>
                                                                <HStack spacing={2}>
                                                                    <Text fontWeight="bold" fontSize="sm">{invoice.invoice_type}</Text>
                                                                    <Badge colorScheme={isPaid ? 'green' : 'orange'} fontSize="2xs">{invoice.status}</Badge>
                                                                    {invoice.pdf_url && <Icon as={FiEye} boxSize="14px" color="brand.500" />}
                                                                </HStack>
                                                                <Text fontWeight="bold" fontSize="md" color={isPaid ? "green.600" : "gray.800"}>{formatCurrency(total)}</Text>
                                                            </Flex>
                                                            <VStack align="stretch" spacing={1.5}>
                                                                <Flex justify="space-between"><Text fontSize="2xs" color="gray.500" fontWeight="medium">Invoice Date</Text><Text fontSize="xs">{formatInvoiceDate(invoice.invoice_date)}</Text></Flex>
                                                                <Flex justify="space-between" align="center"><Text fontSize="2xs" color="gray.500" fontWeight="medium">Due Date</Text><Text fontSize="xs">{formatInvoiceDate(invoice.due_date)}</Text></Flex>
                                                                <Flex justify="space-between" align="center"><Text fontSize="2xs" color="gray.500" fontWeight="medium">Due in</Text>{dueInBadge ? <Badge bg={dueInBadge.bg} color={dueInBadge.color} fontSize="2xs" px={2} py={0.5}>{dueInBadge.label}</Badge> : <Text fontSize="xs" color="gray.500">—</Text>}</Flex>
                                                                {(invoice.invoice_line_items || []).length > 0 && (
                                                                    <Box><Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5}>Description</Text><Text fontSize="xs" noOfLines={2}>{invoice.invoice_line_items.map(i => i.description).join(", ")}</Text></Box>
                                                                )}
                                                                {invoice.pdf_url && (
                                                                    <Flex justify="flex-end" pt={1} onClick={(e) => e.stopPropagation()}>
                                                                        <HStack spacing={1}>
                                                                            <Tooltip label="View PDF" hasArrow>
                                                                                <IconButton icon={<FiEye />} size="xs" variant="ghost" colorScheme="brand"
                                                                                    aria-label="View invoice PDF" onClick={() => handleViewInvoice(invoice)} />
                                                                            </Tooltip>
                                                                            <Tooltip label="Download PDF" hasArrow>
                                                                                <IconButton as="a"
                                                                                    href={`https://drive.google.com/uc?export=download&id=${invoice.pdf_url.match(/\/file\/d\/([^/]+)/)?.[1]}`}
                                                                                    target="_blank" rel="noopener noreferrer"
                                                                                    icon={<FiDownload />} size="xs" variant="ghost" colorScheme="teal"
                                                                                    aria-label="Download invoice PDF" />
                                                                            </Tooltip>
                                                                        </HStack>
                                                                    </Flex>
                                                                )}
                                                            </VStack>
                                                        </Box>
                                                    );
                                                })}
                                            </VStack>

                                            {/* Desktop table view */}
                                            <Box overflowX="auto" display={{ base: "none", md: "block" }}>
                                                <Table variant="simple" size="sm" bg="white">
                                                    <Thead bg="gray.100">
                                                        <Tr>
                                                            <Th>Invoice Date</Th><Th>Due Date</Th><Th>Due in</Th>
                                                            <Th>Description</Th><Th isNumeric>Subtotal</Th>
                                                            <Th isNumeric>GST</Th><Th isNumeric>Total</Th>
                                                            <Th>Status</Th><Th>Actions</Th>
                                                        </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                        {invoices.map((invoice) => {
                                                            const isPaid = invoice.status === 'paid' || invoice.status === 'Paid';
                                                            const subtotal = (invoice.invoice_line_items || []).reduce((s, i) => s + (i.unit_price * i.quantity), 0);
                                                            const gst = invoice.gst_amount || 0;
                                                            const total = subtotal + gst;
                                                            const dueInBadge = getDueInBadge(invoice);
                                                            return (
                                                                <Tr key={invoice.id} bg={isPaid ? "green.50" : "white"}
                                                                    _hover={{ bg: isPaid ? "green.100" : "gray.50", cursor: invoice.pdf_url ? "pointer" : "default" }}
                                                                    borderLeft={isPaid ? "4px solid" : "none"} borderColor={isPaid ? "green.400" : "transparent"}
                                                                    onClick={() => invoice.pdf_url && handleViewInvoice(invoice)} transition="all 0.2s"
                                                                >
                                                                    <Td>{formatInvoiceDate(invoice.invoice_date)}</Td>
                                                                    <Td>{formatInvoiceDate(invoice.due_date)}</Td>
                                                                    <Td>{dueInBadge ? <Badge bg={dueInBadge.bg} color={dueInBadge.color} fontSize="xs" px={2} py={1}>{dueInBadge.label}</Badge> : <Text as="span" color="gray.400">—</Text>}</Td>
                                                                    <Td>{(invoice.invoice_line_items || []).length > 0 ? invoice.invoice_line_items.map(i => i.description).join(", ") : "-"}</Td>
                                                                    <Td isNumeric>{formatCurrency(subtotal)}</Td>
                                                                    <Td isNumeric>{formatCurrency(gst)}</Td>
                                                                    <Td isNumeric fontWeight="bold" color={isPaid ? "green.600" : "gray.800"}>{formatCurrency(total)}</Td>
                                                                    <Td><Badge colorScheme={isPaid ? 'green' : 'orange'} fontSize="sm" px={2} py={1}>{invoice.status}</Badge></Td>
                                                                    <Td onClick={(e) => e.stopPropagation()}>
                                                                        {invoice.pdf_url ? (
                                                                            <HStack spacing={1}>
                                                                                <Tooltip label="View PDF" hasArrow>
                                                                                    <IconButton icon={<FiEye />} size="sm" variant="ghost" colorScheme="brand"
                                                                                        aria-label="View invoice PDF" onClick={() => handleViewInvoice(invoice)} />
                                                                                </Tooltip>
                                                                                <Tooltip label="Download PDF" hasArrow>
                                                                                    <IconButton as="a"
                                                                                        href={`https://drive.google.com/uc?export=download&id=${invoice.pdf_url.match(/\/file\/d\/([^/]+)/)?.[1]}`}
                                                                                        target="_blank" rel="noopener noreferrer"
                                                                                        icon={<FiDownload />} size="sm" variant="ghost" colorScheme="teal"
                                                                                        aria-label="Download invoice PDF" />
                                                                                </Tooltip>
                                                                            </HStack>
                                                                        ) : (
                                                                            <Text fontSize="xs" color="gray.400">Generating…</Text>
                                                                        )}
                                                                    </Td>
                                                                </Tr>
                                                            );
                                                        })}
                                                    </Tbody>
                                                </Table>
                                            </Box>
                                        </VStack>
                                    )}
                                </TabPanel>
                                <TabPanel px={0}>
                                    <CourseEvaluationTab
                                        courseRun={courseMaterials}
                                        courseRunId={courseMaterials?.id || courseRunId}
                                        registration={registration}
                                        onSubmitted={() => {
                                            setEvaluationDue(false);
                                            setBannerDismissed(true);
                                            setEvalRefreshKey((k) => k + 1);
                                        }}
                                    />
                                </TabPanel>
                                <TabPanel px={0}>
                                    <NoticesTab
                                        mode="course"
                                        courseTypeCode={courseMaterials?.course_type_code || courseMaterials?.course_type?.code}
                                        courseRunId={courseMaterials?.id || courseRunId}
                                        showHeader={false}
                                        includeCollege={false}
                                    />
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};
